/**
 * Pilha de camadas sobrepostas (diálogos, folha inferior, popup do mapa).
 *
 * - Escape fecha só a camada de cima.
 * - Enquanto houver uma camada MODAL aberta, todo o resto (o app em `#root` e
 *   as camadas abaixo dela) fica `inert`: não recebe foco, clique nem leitor de
 *   tela — assim o Tab não "escapa" do diálogo.
 * - Ao fechar uma camada modal, o foco volta para onde estava.
 */
import { useEffect, useRef, type RefObject } from 'react';

interface Layer {
  id: number;
  modal: boolean;
  element: () => HTMLElement | null;
  onEscape: () => void;
}

const layers: Layer[] = [];
let seq = 0;
let listening = false;

function onKeyDown(e: KeyboardEvent): void {
  if (e.key !== 'Escape' || layers.length === 0) return;
  // Campo que usa o Escape para si (ex.: fechar as sugestões de uma busca).
  if (e.target instanceof HTMLElement && e.target.dataset.ownEscape === '1') return;
  e.preventDefault();
  e.stopPropagation();
  layers[layers.length - 1].onEscape();
}

function applyInert(): void {
  let topModal = -1;
  layers.forEach((l, i) => {
    if (l.modal) topModal = i;
  });
  const root = document.getElementById('root');
  if (root) root.inert = topModal >= 0;
  layers.forEach((l, i) => {
    const el = l.element();
    if (el) el.inert = i < topModal;
  });
}

/**
 * Voltar do celular com algo aberto por cima da camada de `element` (ex.: o
 * seletor de imagem sobre os ajustes da folha): fecha só a de cima, como o
 * Escape, e devolve true. Sem nada por cima, devolve false.
 */
export function escapeLayerAbove(element: HTMLElement | null): boolean {
  const top = layers[layers.length - 1];
  if (!top || !element || top.element() === element) return false;
  if (!layers.some((l) => l.element() === element)) return false;
  top.onEscape();
  return true;
}

/** Quantas camadas estão abertas (para testes/depuração). */
export function openLayerCount(): number {
  return layers.length;
}

/**
 * Registra uma camada enquanto `open` for true. `ref` aponta para o elemento
 * raiz da camada (fora de `#root`, via portal, para as modais).
 */
export function useModalLayer(
  open: boolean,
  opts: { onEscape: () => void; modal?: boolean; ref?: RefObject<HTMLElement | null> },
): void {
  const onEscapeRef = useRef(opts.onEscape);
  onEscapeRef.current = opts.onEscape;
  const modal = opts.modal ?? true;
  const ref = opts.ref;

  useEffect(() => {
    if (!open) return;
    if (!listening) {
      window.addEventListener('keydown', onKeyDown, true);
      listening = true;
    }
    const previousFocus = modal ? (document.activeElement as HTMLElement | null) : null;
    const layer: Layer = { id: ++seq, modal, element: () => ref?.current ?? null, onEscape: () => onEscapeRef.current() };
    layers.push(layer);
    applyInert();
    return () => {
      const i = layers.indexOf(layer);
      if (i >= 0) layers.splice(i, 1);
      const el = layer.element();
      if (el) el.inert = false;
      applyInert();
      if (previousFocus && previousFocus.isConnected && !previousFocus.closest('[inert]')) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [open, modal, ref]);
}
